import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

const ZONES = [
  { key: 'Z1', label: 'Z1', color: 'bg-blue-500' },
  { key: 'Z2', label: 'Z2', color: 'bg-green-500' },
  { key: 'Z3', label: 'Z3', color: 'bg-yellow-500' },
  { key: 'Z4', label: 'Z4', color: 'bg-orange-500' },
  { key: 'Z5', label: 'Z5', color: 'bg-red-600' }
];

const extractZoneCounts = (hrZones) => {
  // hrZones keys are like "Z1 (Recov) <140" or "Z3 (145-160)".
  const out = { Z1: 0, Z2: 0, Z3: 0, Z4: 0, Z5: 0 };
  if (!hrZones || typeof hrZones !== 'object') return out;

  for (const [k, v] of Object.entries(hrZones)) {
    const m = String(k || '').match(/^Z([1-5])\b/i);
    if (!m) continue;
    const zn = `Z${m[1]}`;
    out[zn] += Number(v) || 0;
  }
  return out;
};

export default function ExecutedHrZonesChart({ hrZones }) {
  const { t } = useTranslation();

  const { pct, total } = useMemo(() => {
    const counts = extractZoneCounts(hrZones);
    const totalCount = Object.values(counts).reduce((a, b) => a + (Number(b) || 0), 0);
    if (!totalCount) return { pct: null, total: 0 };
    const pctObj = Object.fromEntries(
      Object.entries(counts).map(([k, v]) => [k, Math.round((Number(v) / totalCount) * 100)])
    );
    return { pct: pctObj, total: totalCount };
  }, [hrZones]);

  if (!pct || total <= 0) {
    return (
      <div className="mt-4">
        <div className="text-xs font-extrabold uppercase tracking-wide text-gray-600">
          {t('workout.hr_zones')}
        </div>
        <div className="mt-2 text-sm text-gray-600">{t('workout.hr_zones_no_data')}</div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="text-[10px] font-black uppercase tracking-widest text-gray-400 mb-4">
        {t('workout.hr_zones')}
      </div>

      <div className="w-full h-4 rounded-full overflow-hidden bg-gray-50 border border-gray-100 flex mb-4">
        {ZONES.map((z) => {
          const w = pct[z.key] || 0;
          if (w <= 0) return null;
          return <div key={z.key} className={`${z.color}`} style={{ width: `${w}%` }} title={`${z.label}: ${w}%`} />;
        })}
      </div>

      <div className="grid grid-cols-5 gap-2">
        {ZONES.map((z) => (
          <div key={z.key} className="bg-gray-50 border border-gray-100 rounded-xl p-2 text-center">
            <div className="text-[9px] font-black text-gray-400 uppercase tracking-wide mb-0.5">{z.label}</div>
            <div className="text-sm font-black text-gray-900 tabular-nums">{pct[z.key] || 0}%</div>
          </div>
        ))}
      </div>
    </div>
  );
}


